import {
  Body1,
  Button,
  Divider,
  Link,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Subtitle1,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkRegular, CopyRegular } from '@fluentui/react-icons';
import { useState, type ReactNode } from 'react';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', rowGap: '16px' },
  h2: { scrollMarginTop: '16px' },
  h3: { marginTop: '8px' },
  prose: { maxWidth: '760px', lineHeight: tokens.lineHeightBase400 },
  list: {
    maxWidth: '760px',
    ...shorthands.margin(0),
    paddingLeft: '22px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '6px',
    lineHeight: tokens.lineHeightBase400,
    color: tokens.colorNeutralForeground1,
  },
  code: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('1px', '4px'),
  },
  codeBlockWrap: { position: 'relative', maxWidth: '760px' },
  codeBlock: {
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('12px', '14px'),
    ...shorthands.margin(0),
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: '20px',
    color: tokens.colorNeutralForeground1,
  },
  codeBlockWithLang: { paddingTop: '28px' },
  lang: {
    position: 'absolute',
    top: '6px',
    left: '14px',
    fontSize: tokens.fontSizeBase100,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: tokens.colorNeutralForeground3,
  },
  copy: { position: 'absolute', top: '4px', right: '4px' },
  tableWrap: {
    maxWidth: '760px',
    overflowX: 'auto',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  callout: { maxWidth: '760px' },
});

export function H2({ id, children }: { id: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <>
      <Title2 as="h2" id={id} className={styles.h2} block>
        {children}
      </Title2>
      <Divider appearance="subtle" />
    </>
  );
}

export function H3({ id, children }: { id?: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <Subtitle1 as="h3" {...(id ? { id } : {})} className={styles.h3} block>
      {children}
    </Subtitle1>
  );
}

export function P({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return (
    <Body1 as="p" className={styles.prose} block>
      {children}
    </Body1>
  );
}

export function UL({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <ul className={styles.list}>{children}</ul>;
}

export function OL({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <ol className={styles.list}>{children}</ol>;
}

export function Code({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <code className={styles.code}>{children}</code>;
}

export function CodeBlock({ children, lang }: { children: string; lang?: string }) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  return (
    <div className={styles.codeBlockWrap}>
      {lang && <span className={styles.lang}>{lang}</span>}
      <Button
        appearance="subtle"
        size="small"
        className={styles.copy}
        icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
        onClick={() => {
          void navigator.clipboard.writeText(children).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
      <pre className={`${styles.codeBlock} ${lang ? styles.codeBlockWithLang : ''}`}>
        <code>{children}</code>
      </pre>
    </div>
  );
}

type CalloutTone = 'info' | 'warn' | 'danger' | 'ok';
const INTENT = { info: 'info', warn: 'warning', danger: 'error', ok: 'success' } as const;

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: CalloutTone;
  title: string;
  children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <MessageBar intent={INTENT[tone]} className={styles.callout} layout="multiline">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {children}
      </MessageBarBody>
    </MessageBar>
  );
}

export function DocTable({ head, rows }: { head: string[]; rows: (string | ReactNode)[][] }) {
  const styles = useStyles();
  return (
    <div className={styles.tableWrap}>
      <Table size="small" aria-label="Reference table">
        <TableHeader>
          <TableRow>
            {head.map((h) => (
              <TableHeaderCell key={h}>{h}</TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {row.map((cell, j) => (
                <TableCell key={j}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  const styles = useStyles();
  return (
    <section className={styles.section}>
      <H2 id={id}>{title}</H2>
      {children}
    </section>
  );
}

export { Link as DocLink, Subtitle2 };
